import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    ListGroup,
    Modal,
    Spinner,
} from 'react-bootstrap';

import { useEcosystemApi } from '@/shared/services/ecosystemApi';
import { useMembership } from '@/shared/contexts/MembershipContext';
import { getEcosystemDestinationUrl } from '@/shared/utils/ecosystemRouting';

/**
 * YourEcosystems: profile section listing the ecosystems the user belongs to.
 *
 * Data comes from `GET /api/users/me/memberships`, which spans ALL of the
 * user's ecosystems (it is a platform-level, non-tenant-scoped endpoint).
 * Each row shows the ecosystem name (a link into that ecosystem), the user's
 * role in it, the joined date, an "Open" control that enters the ecosystem,
 * and a "Leave" control.
 *
 * Entering an ecosystem is CROSS-TENANT navigation, so the "Open" affordance
 * (and the clickable name) are real anchors built from
 * `getEcosystemDestinationUrl(ecosystem) + '/profile'`, a full-page reload,
 * NOT a SPA `navigate()`/`<Link>`, so `EcosystemProvider` re-bootstraps for the
 * target tenant. The helper resolves the canonical host (custom apex >
 * slug subdomain > path mode). "Open" and "Leave" are kept visually and
 * semantically distinct (primary link vs outline-danger button) so a user
 * never confuses entering an ecosystem with leaving it. Target is the ecosystem's
 * profile, not its dashboard: the member dashboard is platform-level (apex-only),
 * so a link to `<tenant>/dashboard` immediately redirects back out to the apex
 * instead of entering the ecosystem.
 *
 * It also surfaces ecosystems the user could join: the public
 * `GET /api/ecosystems` directory minus the ones already joined, each with a
 * "Join" control, so this section is a complete membership manager.
 *
 * Leaving is consequential, so it is gated behind an in-app confirmation
 * Modal (NOT a blocking `window.confirm`). After a successful join or leave
 * the lists are re-fetched; if the affected ecosystem is the one currently
 * being viewed, MembershipContext is also refreshed so the join banner and
 * member-gated UI stay in sync.
 */
export default function YourEcosystems() {
    const ecosystemApi = useEcosystemApi();
    const { slug: currentSlug, refresh: refreshMembership } = useMembership();

    const [memberships, setMemberships] = useState([]);
    const [joinable, setJoinable] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Slug currently mid-request, so its button can show a spinner / disable.
    const [busySlug, setBusySlug] = useState(null);
    // The membership pending a leave confirmation, or null when the modal is
    // closed. Shape: { ecosystem: { slug, name, ... }, role, joinedAt }.
    const [leaveTarget, setLeaveTarget] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [membershipData, directoryData] = await Promise.all([
                ecosystemApi.fetchMyMemberships(),
                ecosystemApi.fetchEcosystems().catch(() => []),
            ]);
            const myMemberships = membershipData.memberships || [];
            setMemberships(myMemberships);

            // Joinable = the public directory minus ecosystems already joined.
            const joinedSlugs = new Set(
                myMemberships.map((m) => m.ecosystem.slug)
            );
            const directory = Array.isArray(directoryData) ? directoryData : [];
            setJoinable(directory.filter((e) => !joinedSlugs.has(e.slug)));
        } catch (err) {
            console.error(err);
            setError('Failed to load your ecosystems');
        } finally {
            setLoading(false);
        }
    }, [ecosystemApi]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Re-sync MembershipContext when the changed ecosystem is the one being
    // viewed, so the join banner / member-gated UI update immediately.
    const syncCurrentEcosystem = useCallback(
        (changedSlug) => {
            if (changedSlug && changedSlug === currentSlug) {
                refreshMembership();
            }
        },
        [currentSlug, refreshMembership]
    );

    const handleJoin = async (slug) => {
        setBusySlug(slug);
        setError(null);
        try {
            await ecosystemApi.joinEcosystem(slug);
            await loadData();
            syncCurrentEcosystem(slug);
        } catch (err) {
            console.error(err);
            setError('Failed to join ecosystem. Please try again.');
        } finally {
            setBusySlug(null);
        }
    };

    const handleConfirmLeave = async () => {
        if (!leaveTarget) return;
        const slug = leaveTarget.ecosystem.slug;
        setBusySlug(slug);
        setError(null);
        try {
            await ecosystemApi.leaveEcosystem(slug);
            setLeaveTarget(null);
            await loadData();
            syncCurrentEcosystem(slug);
        } catch (err) {
            console.error(err);
            setError('Failed to leave ecosystem. Please try again.');
        } finally {
            setBusySlug(null);
        }
    };

    if (loading) {
        return (
            <Card className="shadow-sm mt-4">
                <Card.Header className="bg-white border-bottom">
                    <strong>Your Ecosystems</strong>
                </Card.Header>
                <Card.Body className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
            <Card className="shadow-sm mt-4" id="ecosystems">
                <Card.Header className="bg-white border-bottom">
                    <strong>Your Ecosystems</strong>
                </Card.Header>
                <Card.Body className="p-0">
                    {error && (
                        <Alert variant="danger" className="m-3">
                            {error}
                        </Alert>
                    )}

                    {memberships.length === 0 ? (
                        <div className="text-center text-muted py-4 px-3">
                            <p className="mb-0">
                                You are not a member of any ecosystem yet.
                            </p>
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {memberships.map((m) => (
                                <ListGroup.Item
                                    key={m.ecosystem.slug}
                                    className="d-flex flex-wrap justify-content-between align-items-center gap-3"
                                >
                                    <div>
                                        {/* The name is a real anchor (full
                                            reload, not SPA nav) so the target
                                            tenant's EcosystemProvider
                                            re-bootstraps. */}
                                        <a
                                            href={
                                                getEcosystemDestinationUrl(
                                                    m.ecosystem
                                                ) + '/profile'
                                            }
                                            className="fw-bold text-decoration-none"
                                        >
                                            {m.ecosystem.name}
                                        </a>{' '}
                                        <Badge
                                            bg={
                                                m.role === 'admin'
                                                    ? 'info'
                                                    : 'secondary'
                                            }
                                        >
                                            {m.role}
                                        </Badge>
                                        <br />
                                        <small className="text-muted">
                                            Joined{' '}
                                            {m.joinedAt
                                                ? new Date(
                                                      m.joinedAt
                                                  ).toLocaleDateString()
                                                : 'unknown date'}
                                        </small>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                        {/* Open = enter the ecosystem
                                            (cross-tenant, full-page reload via
                                            an anchor href). Distinct from
                                            Leave both visually and
                                            semantically. */}
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            className="rounded-pill"
                                            href={
                                                getEcosystemDestinationUrl(
                                                    m.ecosystem
                                                ) + '/profile'
                                            }
                                            aria-label={`Open ${m.ecosystem.name}`}
                                        >
                                            Open
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="rounded-pill"
                                            disabled={
                                                busySlug === m.ecosystem.slug
                                            }
                                            onClick={() => setLeaveTarget(m)}
                                            aria-label={`Leave ${m.ecosystem.name}`}
                                        >
                                            Leave
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}

                    {joinable.length > 0 && (
                        <>
                            <div className="bg-light border-top px-3 py-2">
                                <small className="text-muted fw-bold text-uppercase">
                                    Ecosystems you can join
                                </small>
                            </div>
                            <ListGroup variant="flush">
                                {joinable.map((e) => (
                                    <ListGroup.Item
                                        key={e.slug}
                                        className="d-flex justify-content-between align-items-center gap-3"
                                    >
                                        <div>
                                            <strong>{e.name}</strong>
                                            {e.description && (
                                                <>
                                                    <br />
                                                    <small className="text-muted">
                                                        {e.description}
                                                    </small>
                                                </>
                                            )}
                                        </div>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            className="rounded-pill flex-shrink-0"
                                            disabled={busySlug === e.slug}
                                            onClick={() => handleJoin(e.slug)}
                                        >
                                            {busySlug === e.slug ? (
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                />
                                            ) : (
                                                'Join'
                                            )}
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* In-app leave confirmation: leaving is consequential. */}
            <Modal
                show={Boolean(leaveTarget)}
                onHide={() => setLeaveTarget(null)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Leave ecosystem?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {leaveTarget && (
                        <p className="mb-0">
                            Are you sure you want to leave{' '}
                            <strong>{leaveTarget.ecosystem.name}</strong>? You
                            will lose access to its member features. You can
                            rejoin later.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setLeaveTarget(null)}
                        disabled={Boolean(busySlug)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmLeave}
                        disabled={Boolean(busySlug)}
                    >
                        {busySlug ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    className="me-2"
                                />
                                Leaving…
                            </>
                        ) : (
                            'Leave ecosystem'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
